/*!
 * Copyright (c) REWE Digital GmbH
 */
import { Host, h } from "@stencil/core";
/**
 * Rexus Example component.
 *
 * @example
 *
 * Objects
 * <vx-sorting/>
 *
 */
export class VxSorting {
    constructor() {
        /**
         *  Handle Click outside to close popup.
         */
        this.handleOutsideClick = (e) => {
            const parentElement = document.getElementById('vx-sorting');
            if (!parentElement.contains(e.target)) {
                this.showPopup = false;
            }
        };
        /**
         * Handle Click on sorting option.
         */
        this.handleOptionClick = (e) => {
            var _a;
            this.setOption((_a = e.target) === null || _a === void 0 ? void 0 : _a.dataset.id);
            e.preventDefault();
        };
        /**
         * Handle KeyPress on trigger button.
         */
        this.handleKeyPress = (e) => {
            var _a, _b;
            let nextOption;
            const target = e.target;
            const isButton = target.tagName === 'BUTTON';
            switch (e.key) {
                case 'Escape':
                    if (this.showPopup) {
                        this.showPopup = false;
                        this.host.querySelector('button').focus();
                        e.preventDefault();
                    }
                    break;
                case 'Tab':
                    this.showPopup = false;
                    break;
                case 'ArrowDown':
                    if (isButton) {
                        nextOption = this.host.querySelector('.vx-sorting__select--desktop ul li:first-child a');
                    }
                    else {
                        nextOption = (_a = target.parentElement.nextElementSibling) === null || _a === void 0 ? void 0 : _a.querySelector('a');
                    }
                    if (nextOption) {
                        nextOption.focus();
                        e.preventDefault();
                    }
                    break;
                case 'ArrowUp':
                    if (!isButton) {
                        nextOption = (_b = target.parentElement.previousElementSibling) === null || _b === void 0 ? void 0 : _b.querySelector('a');
                    }
                    if (nextOption) {
                        nextOption.focus();
                        e.preventDefault();
                    }
                    break;
                case 'Enter':
                    if (!isButton) {
                        this.setOption(target.getAttribute('data-id'));
                        e.preventDefault();
                    }
                    break;
            }
        };
        this.sortingLabel = 'Sorting';
        this.sortingOptions = undefined;
        this.loading = false;
        this.showPopup = false;
    }
    /**
     *  Set selected option and trigger event.
     */
    setOption(value) {
        this.sortingChanged.emit({ sort: parseInt(value, 10) });
        this.loading = true;
    }
    /**
     *  Handle Click to open popup.
     */
    handleClick(e) {
        const parentElement = document.getElementById('vx-sorting');
        if (e.target.tagName !== 'SELECT') {
            if (parentElement.contains(e.target)) {
                this.showPopup = !this.showPopup;
            }
            else {
                this.showPopup = false;
            }
        }
    }
    /**
     * Triggered after component load.
     */
    componentDidLoad() {
        window.addEventListener('click', this.handleOutsideClick);
    }
    /**
     * Triggered if component unmount.
     */
    disconnectedCallback() {
        window.removeEventListener('click', this.handleOutsideClick);
    }
    /**
     * Watch for sortingOptions change to reset states.
     */
    watchHandler() {
        this.showPopup = false;
        this.loading = false;
    }
    /**
     * Renders the component.
     *
     * @returns {JSX.Element} - The rendered component.
     */
    render() {
        var _a, _b, _c, _d;
        return (h(Host, { key: '48c6107d515e4aba4c3ec98cb9b28b1fc55b5288', id: 'vx-sorting' }, h("div", { key: '8afd6e112eda65c03386decd234e7c372e789af1', class: {
                'vx-sorting__label': true,
                'vx-sorting__label--open': this.showPopup,
            } }, h("button", { key: 'de2ffa31cfc15ddcb4b0b1319bf1ee0a96194609', type: "button", id: "sortingbutton", "aria-haspopup": "true", "aria-expanded": this.showPopup, "aria-controls": "sortingoptions", class: "vx-sorting__label-text", onKeyDown: this.handleKeyPress }, this.sortingLabel), h("div", { key: '85de39eace4551398b397f540e77bb496b80fa9b', class: {
                'vx-sorting__select--desktop': true,
                'vx-sorting__loading-visible': this.loading,
            } }, h("ul", { key: 'dcef4e8ce8d4ba69af086858ed374843fd457b20', id: "sortingoptions", role: "menu", "aria-labelledby": "sortingbutton", "aria-activedescendant": `sortingoption${(_b = (_a = this.sortingOptions) === null || _a === void 0 ? void 0 : _a.find(option => option.selected)) === null || _b === void 0 ? void 0 : _b.id}`, onClick: ev => ev.stopPropagation() }, (_c = this.sortingOptions) === null || _c === void 0 ? void 0 : _c.map(option => (h("li", null, h("a", { id: `sortingoption${option.id}`, role: "menuitem", tabindex: "-1", href: "#", class: {
                'vx-sorting__option': true,
                'vx-sorting__selected-option': (option.selected),
            }, "data-id": option.id, onClick: this.handleOptionClick, onKeyDown: this.handleKeyPress }, option.label))))), h("div", { key: '0ce3f8b44289249c524b269f60b01aa6080bac92', class: 'vx-sorting__loading-overlay' }, this.loading && h("vx-loading-spinner", null))), h("select", { key: 'dcbfe896598c4539882d435a606a76fd42b29900', onChange: (e) => this.setOption(e.target.value), class: 'vx-sorting__select vx-sorting__select--mobile' }, (_d = this.sortingOptions) === null || _d === void 0 ? void 0 : _d.map(option => (h("option", { value: option.id, selected: option.selected }, option.label)))))));
    }
    static get is() { return "vx-sorting"; }
    static get originalStyleUrls() {
        return {
            "$": ["vx-sorting.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["vx-sorting.css"]
        };
    }
    static get properties() {
        return {
            "sortingLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Sorting Label."
                },
                "attribute": "sorting-label",
                "reflect": false,
                "defaultValue": "'Sorting'"
            },
            "sortingOptions": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "SortingOptions."
                },
                "attribute": "sorting-options",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "loading": {},
            "showPopup": {}
        };
    }
    static get events() {
        return [{
                "method": "sortingChanged",
                "name": "sortingChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "ChangeSorting."
                },
                "complexType": {
                    "original": "{sort: number}",
                    "resolved": "{ sort: number; }",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "host"; }
    static get watchers() {
        return [{
                "propName": "sortingOptions",
                "methodName": "watchHandler"
            }];
    }
    static get listeners() {
        return [{
                "name": "click",
                "method": "handleClick",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=vx-sorting.js.map
